import { Container, Graphics, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import gsap from 'gsap'
import { delay } from '../../framework/core/async'
import { layoutMetrics, type Orientation, type Resizable } from '../../framework/render/design'
import { SymbolPool } from '../../slotbase/SymbolPool'
import { MULTIPLIER_VALUES, multiplierTexture } from '../assets/MultiplierTextures'
import { getAvailableMultiplierValues, MIN_LINE_MATCH, MULTIPLIER_WHEEL_VALUE } from '../constants'
import { createSymbolSpine } from '../assets/SymbolSpine'
import { uiTexture } from '../assets/UiTextures'
import type { RuntimeConfig } from '../config/gameConfig'
import type { SlotResult } from '../model/SlotResult'
import { ReelView } from './ReelView'
import { createMultiplierStrip, symbolStrip } from './ReelStrip'

// 轉輪群組（盤面）— 對應 Unity SlotReels + Slot001_MultiplierReels。
// 配置：左側 N 個符號轉輪 + 右側 1 個倍數轉輪（共 N+1 欄），全部置於 pic_main 框內。
const ROWS_PER_SECOND = 20
const STOP_STAGGER_MS = 140
const STOP_STAGGER_TURBO_MS = 35
// 倍數中間金框相對「一格」的放大倍率（1.0=剛好一格；>1 框外緣超出格線、更明顯框住）
const MULT_FRAME_FILL = 1.35
// ExtraBet 升級倍數幣飛入特效參數。
const EXTRA_FX_COIN_COUNT = 6
const EXTRA_FX_FLY_SECONDS = 0.55
const EXTRA_FX_STAGGER = 0.08
// 飛行幣大小：起始佔一格比例（略大於一格，醒目）、抵達時相對起始的縮放因子。
const EXTRA_FX_START_RATIO = 1.05
const EXTRA_FX_END_FACTOR = 0.6
// pic_main 轉輪窗在整張框貼圖中的矩形（佔整框寬高比例；實測自 frame.png 704×632）
const FRAME_WINDOW = { x: 0.138, y: 0.255, w: 0.707, h: 0.505 } as const
// 框最大寬度佔螢幕比例（橫版留出左右轉盤/老虎；直版幾乎滿版）
const FRAME_MAX_WIDTH_RATIO: Readonly<Record<Orientation, number>> = {
  landscape: 0.62,
  portrait: 0.96,
}

export class ReelsView extends Container implements Resizable {
  private readonly config: RuntimeConfig
  // ExtraBet 狀態取值函式（讀 PlayerState.extraBet）— 決定倍數轉輪可顯示的倍數幣集合。
  private readonly isExtraBet: () => boolean
  private readonly pool = new SymbolPool()
  private readonly frame = new Sprite()
  private readonly reelsLayer = new Container()
  private readonly maskShape = new Graphics()
  private readonly winFxLayer = new Container() // 中獎 Spine 特效（不裁切，疊在最上）
  private readonly winSpines: Spine[] = []
  private readonly reels: ReelView[] = [] // 符號轉輪
  private readonly multReel: ReelView // 倍數轉輪（1×rowCount）
  private readonly multFrame = new Sprite() // 倍數轉輪中間格金框（Unity pic_extraFrame，框住生效倍數）
  private cellW = 0
  private cellH = 0
  // 主盤框螢幕矩形（供左側 Bonus 立輪以框左緣為基準定位）
  private frameScreenCx = 0
  private frameScreenCy = 0
  private frameScreenW = 0
  private frameScreenH = 0

  constructor(config: RuntimeConfig, isExtraBet: () => boolean) {
    super()
    this.config = config
    this.isExtraBet = isExtraBet
    this.frame.anchor.set(0.5)
    this.frame.texture = uiTexture('frame')
    // 框（含轉輪窗木紋底）在後 → 符號/倍數疊在窗上 → 遮罩裁切 → 中獎特效在最上
    this.addChild(this.frame, this.reelsLayer, this.maskShape, this.winFxLayer)
    this.reelsLayer.mask = this.maskShape

    for (let reel = 0; reel < config.reelCount; reel++) {
      const view = new ReelView(config.rowCount, this.pool, symbolStrip)
      this.reels.push(view)
      this.reelsLayer.addChild(view)
    }
    // 倍數轉輪不用半透明高亮底板；改在中間格疊金框（pic_extraFrame）框住生效倍數。
    // 中間格內容＝後端回傳的生效倍數（stopWith 以 multiplierColumn(active) 對位）。
    this.multReel = new ReelView(config.rowCount, this.pool, createMultiplierStrip(isExtraBet))
    this.reelsLayer.addChild(this.multReel)
    // 金框放在不裁切的 winFxLayer（與 reelsLayer 同 position），放大後框邊才不會被窗遮罩裁掉。
    this.multFrame.anchor.set(0.5)
    this.multFrame.texture = uiTexture('mult_frame')
    this.winFxLayer.addChild(this.multFrame)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.position.set(0, 0)
    const metrics = layoutMetrics(orientation)
    const topOffset = height * metrics.topReserve
    const usableH = height * (1 - metrics.barHeight) - topOffset

    // 1) 依可用區決定框大小（保持貼圖長寬比）
    const tex = this.frame.texture
    const frameAspect = tex.width > 0 ? tex.width / tex.height : 704 / 632
    let frameH = usableH
    let frameW = frameH * frameAspect
    const maxW = width * FRAME_MAX_WIDTH_RATIO[orientation]
    if (frameW > maxW) {
      frameW = maxW
      frameH = frameW / frameAspect
    }
    this.frame.scale.set(tex.width > 0 ? frameW / tex.width : 1)
    const frameCx = width / 2
    // 橫/直版皆垂直置中於可用區（頂部保留與底部操作列之間），框上下保留空間給 Bonus/老虎/操作列
    const frameCy = topOffset + usableH / 2
    this.frame.position.set(frameCx, frameCy)
    this.frameScreenCx = frameCx
    this.frameScreenCy = frameCy
    this.frameScreenW = frameW
    this.frameScreenH = frameH

    // 2) 框內的轉輪窗矩形（螢幕座標）
    const winLeft = frameCx - frameW / 2 + FRAME_WINDOW.x * frameW
    const winTop = frameCy - frameH / 2 + FRAME_WINDOW.y * frameH
    const winW = FRAME_WINDOW.w * frameW
    const winH = FRAME_WINDOW.h * frameH

    // 3) 盤面填滿窗口：每欄寬 winW/欄數、每列高 winH/列數（非正方，符號等比置中）
    const totalCols = this.config.reelCount + 1
    this.cellW = winW / totalCols
    this.cellH = winH / this.config.rowCount

    for (let reel = 0; reel < this.reels.length; reel++) {
      this.reels[reel].setCell(this.cellW, this.cellH)
      this.reels[reel].position.set(reel * this.cellW, 0)
    }
    this.multReel.setCell(this.cellW, this.cellH)
    this.multReel.position.set(this.config.reelCount * this.cellW, 0)

    // 金框置於倍數欄正中間格中心，外緣依 MULT_FRAME_FILL 貼齊／略大於中間格背板。
    this.multFrame.position.set(
      this.config.reelCount * this.cellW + this.cellW / 2,
      Math.floor(this.config.rowCount / 2) * this.cellH + this.cellH / 2,
    )
    const mtex = this.multFrame.texture
    if (mtex.width > 0 && mtex.height > 0) {
      this.multFrame.scale.set(
        (this.cellW * MULT_FRAME_FILL) / mtex.width,
        (this.cellH * MULT_FRAME_FILL) / mtex.height,
      )
    }

    this.reelsLayer.position.set(winLeft, winTop)
    this.winFxLayer.position.set(winLeft, winTop)
    this.maskShape.clear()
    this.maskShape.rect(winLeft, winTop, winW, winH).fill({ color: 0xffffff })
  }

  /** 主盤框的螢幕矩形（圓心座標 + 寬高），供左側 Bonus 立輪定位。 */
  get frameRect(): { readonly cx: number; readonly cy: number; readonly width: number; readonly height: number } {
    return {
      cx: this.frameScreenCx,
      cy: this.frameScreenCy,
      width: this.frameScreenW,
      height: this.frameScreenH,
    }
  }

  /** 目前畫面實際顯示的符號盤面（各符號轉輪的 finalIds）— 診斷用。 */
  get displayedBoard(): number[][] {
    return this.reels.map((reel) => [...reel.currentIds])
  }

  /** 不經動畫直接顯示盤面（初始狀態）。 */
  setStatic(board: ReadonlyArray<ReadonlyArray<number>>): void {
    for (let reel = 0; reel < this.reels.length; reel++) {
      this.reels[reel].setStatic(board[reel] ?? [])
    }
    this.multReel.setStatic(this.multiplierColumn(this.headlineMultiplier()))
  }

  startSpin(): void {
    this.clearEffects()
    const speed = this.cellH * ROWS_PER_SECOND
    for (const reel of this.reels) reel.startSpin(speed)
    this.multReel.startSpin(speed)
  }

  /** 交錯停輪並對位到結果盤面（含倍數轉輪）。 */
  async stopWith(result: SlotResult, turbo: boolean): Promise<void> {
    const board = result.mgResult.mgSymbol
    const stagger = turbo ? STOP_STAGGER_TURBO_MS : STOP_STAGGER_MS
    const stops: Promise<void>[] = []
    for (let reel = 0; reel < this.reels.length; reel++) {
      if (reel > 0) await delay(stagger)
      stops.push(this.reels[reel].requestStop(board[reel] ?? []))
    }
    await delay(stagger)
    const active = this.activeMultiplier(result.mgResult.mgMultiplier)
    stops.push(this.multReel.requestStop(this.multiplierColumn(active)))
    await Promise.all(stops)
  }

  // mgLineCount[i] = 第 i 條線的「連線符號總數」（後端語意，3 連 = 3；非中獎為 0）。
  // 由左對齊，高亮前 N 欄（N = 連線數，最少 MIN_LINE_MATCH）。
  highlightWins(result: SlotResult): void {
    const counts = result.mgResult.mgLineCount
    const lines = this.config.paylines
    let anyWin = false
    for (let i = 0; i < lines.length; i++) {
      if ((counts[i] ?? 0) >= MIN_LINE_MATCH) {
        anyWin = true
        break
      }
    }
    if (!anyWin) return

    const board = result.mgResult.mgSymbol
    const seen = new Set<string>()
    for (const reel of this.reels) reel.dimAll(true)
    for (let i = 0; i < lines.length; i++) {
      const matchCount = counts[i] ?? 0
      if (matchCount < MIN_LINE_MATCH) continue
      const runLength = Math.min(matchCount, this.reels.length)
      const line = lines[i]
      for (let reel = 0; reel < runLength; reel++) {
        const row = line[reel] ?? 0
        this.reels[reel].markWin(row)
        const key = `${reel},${row}`
        if (!seen.has(key)) {
          seen.add(key)
          this.spawnWinFx(reel, row, board[reel]?.[row] ?? 0)
        }
      }
    }
  }

  clearEffects(): void {
    for (const reel of this.reels) reel.clearEffects()
    this.multReel.clearEffects()
    for (const spine of this.winSpines) {
      this.winFxLayer.removeChild(spine)
      spine.destroy()
    }
    this.winSpines.length = 0
  }

  private spawnWinFx(reel: number, row: number, symbolId: number): void {
    const spine = createSymbolSpine(symbolId)
    if (spine === null) return
    const data = spine.skeleton.data
    const span = Math.max(data.width, data.height)
    if (span > 0) spine.scale.set((Math.min(this.cellW, this.cellH) * 1.35) / span)
    spine.position.set(reel * this.cellW + this.cellW / 2, row * this.cellH + this.cellH / 2)
    this.winFxLayer.addChild(spine)
    this.winSpines.push(spine)
  }

  update(deltaSeconds: number): void {
    for (const reel of this.reels) reel.update(deltaSeconds)
    this.multReel.update(deltaSeconds)
  }

  /**
   * ExtraBet 切換時即刻更新第四輪靜止顯示的倍數幣（X1↔X15）——
   * 對應 Unity ExtbetEffect.ChangeReelSymbol 的預顯示序列更新。
   * 中間格展示該模式最高倍數（一般=X10 / ExtraBet=X15），上下格依新模式隨機。
   * 僅在閒置時呼叫（旋轉中 ExtraBet 開關已鎖定），不會打斷停輪流程。
   */
  refreshMultiplierStrip(): void {
    if (this.multReel.isSpinning) return // 旋轉/停輪中不打斷流程（UI 另已鎖定按鈕，此為深度防禦）
    this.multReel.setStatic(this.multiplierColumn(this.headlineMultiplier()))
  }

  /**
   * ExtraBet 啟用特效：倍數幣由盤面各處飛入倍數轉輪 — 對應 Unity ExtbetEffect。
   * 回傳的 Promise 在所有倍數幣抵達第四輪（飛到點）時 resolve，
   * 供呼叫端在「升級幣抵達後」才切換第四輪輪帶顯示（X15 出現）。
   */
  playExtraBetFx(): Promise<void> {
    if (this.cellW <= 0) return Promise.resolve()
    const targetX = this.config.reelCount * this.cellW + this.cellW / 2
    const targetY = (this.config.rowCount * this.cellH) / 2
    const size = Math.min(this.cellW, this.cellH)
    const baseMax = size * EXTRA_FX_START_RATIO
    const arrivals: Promise<void>[] = []
    for (let i = 0; i < EXTRA_FX_COIN_COUNT; i++) {
      const tex = multiplierTexture(this.randomMultiplier())
      if (tex === null) continue
      const coin = new Sprite(tex)
      coin.anchor.set(0.5)
      const scale = baseMax / Math.max(tex.width, tex.height)
      coin.scale.set(scale)
      coin.alpha = 0
      coin.position.set(
        this.cellW * Math.random() * this.config.reelCount,
        this.cellH * Math.random() * this.config.rowCount,
      )
      this.winFxLayer.addChild(coin)
      const arrival = new Promise<void>((resolve) => {
        const timeline = gsap.timeline({ delay: i * EXTRA_FX_STAGGER, onComplete: () => coin.destroy() })
        timeline.to(coin, { alpha: 1, duration: 0.15 }, 0)
        timeline.to(
          coin.position,
          { x: targetX, y: targetY, duration: EXTRA_FX_FLY_SECONDS, ease: 'power2.in', onComplete: resolve },
          0,
        )
        timeline.to(
          coin.scale,
          {
            x: scale * EXTRA_FX_END_FACTOR,
            y: scale * EXTRA_FX_END_FACTOR,
            duration: EXTRA_FX_FLY_SECONDS,
            ease: 'power2.in',
          },
          0,
        )
        timeline.to(coin, { alpha: 0, duration: 0.2 }, EXTRA_FX_FLY_SECONDS - 0.13)
      })
      arrivals.push(arrival)
    }
    return Promise.all(arrivals).then(() => undefined)
  }

  /**
   * 兌獎乘倍演出：倍數幣自倍數轉輪中間格飛入盤面中央並放大 —— 對應 Unity Slot001_WinUI
   * 的 ShowMultiplierAnimation（倍數符號 fadein → fly → fadeout）。
   * 回傳的 Promise 在倍數幣抵達中央（即將套用乘倍）時 resolve；其後的脈衝/淡出在背景進行，不阻塞計數。
   */
  async playMultiplierApplyFx(multiplier: number, fast: boolean): Promise<void> {
    if (this.cellW <= 0) return
    const tex = multiplierTexture(multiplier)
    if (tex === null) return

    // 起點：倍數轉輪中間格中心（與 multFrame 對位一致）；終點：符號盤面區域中央。
    const fromX = this.config.reelCount * this.cellW + this.cellW / 2
    const fromY = Math.floor(this.config.rowCount / 2) * this.cellH + this.cellH / 2
    const centerX = (this.config.reelCount * this.cellW) / 2
    const centerY = (this.config.rowCount * this.cellH) / 2
    const size = Math.min(this.cellW, this.cellH)
    const baseScale = (size * 0.9) / Math.max(tex.width, tex.height)

    const coin = new Sprite(tex)
    coin.anchor.set(0.5)
    coin.scale.set(baseScale)
    coin.position.set(fromX, fromY)
    this.winFxLayer.addChild(coin)

    const flyTime = fast ? 0.3 : 0.55
    await new Promise<void>((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          // 抵達中央：再放大一下並淡出消失（背景進行，不阻塞乘倍計數）
          gsap
            .timeline({ onComplete: () => coin.destroy() })
            .to(coin.scale, { x: baseScale * 2.4, y: baseScale * 2.4, duration: 0.18, ease: 'back.out(2)' })
            .to(coin, { alpha: 0, duration: 0.24 }, '<0.05')
          resolve()
        },
      })
      tl.to(coin.position, { x: centerX, y: centerY, duration: flyTime, ease: 'power2.inOut' }, 0)
      tl.to(coin.scale, { x: baseScale * 1.8, y: baseScale * 1.8, duration: flyTime, ease: 'power2.out' }, 0)
    })
  }

  /** 倍數轉輪的一欄內容：上下為隨機倍數，中間為實際倍數。 */
  private multiplierColumn(active: number): number[] {
    return [this.randomMultiplier(), active, this.randomMultiplier()]
  }

  private randomMultiplier(): number {
    // 旋轉/填充的隨機倍數幣須遵守當前模式可用集合（一般含 X1、ExtraBet 含 X15）。
    const values = getAvailableMultiplierValues(this.isExtraBet())
    return values[Math.floor(Math.random() * values.length)]!
  }

  /** 該模式的代表倍數＝最高可用倍數（一般=X10、ExtraBet=X15）；閒置時放中間格展示。 */
  private headlineMultiplier(): number {
    return Math.max(...getAvailableMultiplierValues(this.isExtraBet()))
  }

  private activeMultiplier(value: number): number {
    // Wheel(0)：停在 Wheel 符號（進 Bonus），中間格顯示轉盤圖。
    if (value === MULTIPLIER_WHEEL_VALUE) return MULTIPLIER_WHEEL_VALUE
    return (MULTIPLIER_VALUES as readonly number[]).includes(value) ? value : 1
  }

}
